/* eslint-disable max-len */
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { FaAngleUp, FaChevronUp } from 'react-icons/fa';
import AllViewers from './AllViewers';
import { getReaction } from '../../../../../../actions/feeds_actions_new';
import { catchErr } from '../../../../../../../libs/HelperContent';
import NoData from '../../../../../../../components/Common/NoData';
import { LanguageContext } from '../../../../../../../libs/Context';

const tabData = [
	{ key: 'r1', icon: '/images/icons/thumbsup.svg', alt: 'thumbs up', size: 24 },
	{ key: 'r2', icon: '/images/icons/star.svg', alt: 'star', size: 20 },
	{ key: 'r3', icon: '/images/icons/redheart.svg', alt: 'heart', size: 18 },
	{ key: 'r4', icon: '/images/icons/clap.svg', alt: 'clap', size: 24 },
	{ key: 'r5', icon: '/images/icons/shock.svg', alt: 'shock', size: 20 }
];

const tabsStyle = {
	justifyContent        : 'flex-start',
	'& .MuiTabs-scroller' : {
		display        : 'flex',
		justifyContent : 'flex-start'
	},
	'& .MuiTabs-scrollButtons' : {
		width : 'auto'
	},
	'& .MuiTabs-scrollButtons.Mui-disabled' : {
		opacity : 0.3
	}
};

const ReactionView = ({ setEventState, getData, postData }) =>
{
	const { language } = useContext(LanguageContext);
	const [ value, setValue ] = useState(0);
	const [ loading, setLoading ] = useState(false);
	const [ reactedData, setReactedData ] = useState([]);
	const [ emojiCount, setEmojiCount ] = useState({ r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 });
	const [ error, setError ] = useState<any>(null);

	const tabRecRef = useRef('');

	const { eventId, reactionData } = getData;
	const currentId = eventId || postData?.id;
	const rType = reactionData.length > 0 ? reactionData[0]?.type : postData?.type ?? null;

	const QUERY_PARAMS = useMemo(() => ({
		id   : currentId,
		type : tabRecRef.current || rType
	}), [ currentId, tabRecRef.current, rType ]);

	const handleChange = (event, tabValue) =>
	{
		setValue(tabValue);
		tabRecRef.current = tabData[tabValue]?.key || '';
	};

	const fetchEmojiCount = useCallback(async () =>
	{
		if (!loading)
		{
			setLoading(true);
			try
			{
				const res = await getReaction({ ...QUERY_PARAMS, emojiCount: true });

				if (res?.status)
				{
					setEmojiCount(res?.count[0]);
				}
			}
			catch (err)
			{
				setError('Failed to fetch emoji count');
				catchErr(err);
			}
			finally
			{
				setLoading(false);
			}
		}
	}, [ loading, QUERY_PARAMS ]);

	const fetchUserCount = useCallback(async () =>
	{
		if (!loading)
		{
			setLoading(true);
			try
			{
				const res = await getReaction({ ...QUERY_PARAMS, count: true });

				if (res?.status)
				{
					setEmojiCount((prev) => ({ ...prev, ...res?.count[0] }));

					if (tabRecRef?.current === '')
					{
						const findFrstValGrtr:any = Object.entries(res?.count?.[0] ?? {})?.findIndex(([ key, val ]) => Number(val) > 0);

						setValue(findFrstValGrtr || 0);
					}
				}
			}
			catch (err)
			{
				setError('Failed to fetch user count');
				catchErr(err);
			}
			finally
			{
				setLoading(false);
			}
		}
	}, [ loading, QUERY_PARAMS ]);

	const fetchReactionData = useCallback(async () =>
	{
		if (!loading)
		{
			setLoading(true);
			try
			{
				const res = await getReaction({ ...QUERY_PARAMS, skip: 0, limit: 10 });

				if (res?.status)
				{
					setReactedData(res?.data);
					fetchUserCount();
				}
			}
			catch (err)
			{
				setError('Failed to fetch reaction data');
				catchErr(err);
			}
			finally
			{
				setLoading(false);
			}
		}
	}, [ loading, QUERY_PARAMS, fetchUserCount ]);

	useEffect(() =>
	{
		if (postData?.key)
		{
			fetchEmojiCount();
		}
		if (value >= 0 && value <= 4)
		{
			fetchReactionData();
		}
	}, [ value ]);

	const renderTabs = tabData.map((tab, index) => (
		<Tab
			key={tab?.key}
			icon={<img src={tab?.icon} alt={tab?.alt} width={tab?.size} height={tab?.size} />}
			aria-label={tab?.alt}
			value={index}
			iconPosition='start'
			label={emojiCount[tab?.key]}
			sx={{ minWidth: 55 }}
		/>
	));

	return (
		<Grid xs={12} width='var(--percent100)' sx={{ py: 2 }}>
			<Stack direction='row' justifyContent='space-between' alignItems='center' px='var(--pixel8)'>
				<Typography variant='subtitle1' fontWeight='var(--font500)'>{language.reactions}</Typography>
				<FaChevronUp
					color='#536471'
					style={{ cursor: 'pointer' }}
					fontSize='var(--pixel18)'
					onClick={() => setEventState((prev:any) => ({ ...prev, openReaction: false }))}
				/>

			</Stack>
			<Stack>
				<Tabs
					value={value}
					onChange={handleChange}
					aria-label='reaction tabs'
					variant='scrollable'
					sx={tabsStyle}
				>
					{renderTabs}
				</Tabs>
			</Stack>
			{error && <Typography color='error'>{error}</Typography>}
			{reactedData && reactedData.length > 0
				? (
					<AllViewers reactedData={reactedData} key={value} />
				)
				: (
					<div className='flexCol pdngXS justifyCntr fullHeight'>
						<NoData noDataHead={language?.nodataFound} color='lightTxt' src1='/images/Bpsnodata.svg' />
					</div>
				)}
		</Grid>
	);
};

export default ReactionView;
